import { useCallback, useMemo, useEffect, useState } from 'react';
import { getIconCategoryColor } from '../components/common/iconCategory';

const GRID_SIZE = 20;
const CONTAINER_WIDTH = 2000;
const CONTAINER_HEIGHT = 2000;

/**
 * Custom hook for grid and connection calculations
 */
export const useGridCalculations = (flowItems, showExpansionLines = true) => {
  const [domPositionsReady, setDomPositionsReady] = useState(false);
  const [componentPositions] = useState(() => new Map()); // Store positions by component ID + panel ID

  const getSharedMainConnectorY = useCallback((panels) => {
    if (!panels.length) return null;

    return Math.min(...panels.map((panel) => panel.y + panel.height / 2));
  }, []);
  
  // Generate grid dots based on container size
  const generateGridDots = useCallback(() => {
    const dots = [];
    
    // Calculate bounds based on panel positions
    let minX = 0;
    let minY = 0;
    let maxX = CONTAINER_WIDTH;
    let maxY = CONTAINER_HEIGHT;
    
    if (flowItems.length > 0) {
      const panels = flowItems.filter(item => item.type === 'panel');
      const plusSigns = flowItems.filter(item => item.type === 'plus');
      
      if (panels.length > 0 || plusSigns.length > 0) {
        // Include both panels and plus signs in bounds calculation
        const allItems = [...panels, ...plusSigns];
        minX = Math.min(...allItems.map(item => item.x));
        minY = Math.min(...allItems.map(item => item.y));
        maxX = Math.max(...allItems.map(item => item.type === 'panel' ? item.x + item.width : item.x + 32));
        maxY = Math.max(...allItems.map(item => item.type === 'panel' ? item.y + item.height : item.y + 32));
        
        // Add padding around the items
        const padding = 200;
        // Allow negative values for minX/minY to support centering
        minX = minX - padding;
        minY = minY - padding;
        maxX = maxX + padding;
        maxY = maxY + padding;
      }
    }
    
    // Calculate grid size accounting for negative positions
    const gridWidth = Math.max(CONTAINER_WIDTH, maxX - minX);
    const gridHeight = Math.max(CONTAINER_HEIGHT, maxY - minY);
    
    /* const dotsX = Math.floor(gridWidth / GRID_SIZE);
    const dotsY = Math.floor(gridHeight / GRID_SIZE);
    
    for (let i = 0; i <= dotsX; i++) {
      for (let j = 0; j <= dotsY; j++) {
        dots.push({
          key: `${i}-${j}`,
          x: i * GRID_SIZE,
          y: j * GRID_SIZE,
        });
      }
    } */
    
    // Return minX and minY for viewBox positioning
    return { dots, width: gridWidth, height: gridHeight, minX, minY };
  }, [flowItems]);

  // Generate connection lines between panels and plus signs
  const generateConnections = useCallback(() => {
    const connections = [];

    const getFallbackComponentBounds = (panel, component) => {
      const componentIndex = panel.components.indexOf(component);
      if (componentIndex === -1) {
        return {
          leftX: panel.x + 12,
          rightX: panel.x + panel.width - 12,
          centerY: panel.y + panel.height / 2
        };
      }

      const headerHeight = 36;
      const imageHeight = 60;
      const imageMarginBottom = 12;
      const panelPaddingTop = 12;
      const panelPaddingX = 12;
      const componentGap = 8;
      const availableTop = panel.y + panelPaddingTop + headerHeight + imageHeight + imageMarginBottom;
      const componentCount = Math.max(panel.components.length, 1);
      const availableHeight = Math.max(
        panel.height - (availableTop - panel.y) - 12,
        componentCount * 40
      );
      const componentHeight = Math.max(
        (availableHeight - (componentGap * (componentCount - 1))) / componentCount,
        24
      );

      return {
        leftX: panel.x + panelPaddingX,
        rightX: panel.x + panel.width - panelPaddingX,
        centerY: availableTop + (componentIndex * (componentHeight + componentGap)) + (componentHeight / 2)
      };
    };
    
    const panels = flowItems
      .filter((item) => item.type === "panel")
      .sort((a, b) => a.x - b.x);
    const sharedMainConnectorY = getSharedMainConnectorY(panels);
    
    panels.forEach((panel, index) => {
      const nextPanel = panels[index + 1];
      
      if (nextPanel) {
        const startX = panel.x + panel.width;
        const endX = nextPanel.x;
        
        // Main step connectors should stay on a single shared baseline even when
        // adjacent panels have different heights.
        const fixedY = sharedMainConnectorY ?? (panel.y + panel.height / 2);
        
        // Only add connection if distance > threshold (reduced from 50 to 10)
        const distance = Math.abs(endX - startX);
        if (distance > 10) {
          // Simple horizontal line
          connections.push({
            key: `conn-${panel.id}-${nextPanel.id}`,
            x1: startX,
            y1: fixedY,
            x2: endX,
            y2: fixedY,
            isJustDot: false
          });
          
          // Add connection dots
          connections.push({
            key: `dot-start-${panel.id}`,
            x: startX,
            y: fixedY,
            isJustDot: true
          });
          
          connections.push({
            key: `dot-end-${nextPanel.id}`,
            x: endX,
            y: fixedY,
            isJustDot: true
          });
        }
      }
    });
    
    // Add expansion lines if enabled
    if (showExpansionLines) {
      // Group panels by component ID to find components that span multiple steps
      const componentPanelGroups = new Map();
      
      panels.forEach((panel) => {
        if (panel.components) {
          panel.components.forEach(component => {
            const compId = component.id;
            if (!componentPanelGroups.has(compId)) {
              componentPanelGroups.set(compId, []);
            }
            componentPanelGroups.get(compId).push({
              panel,
              component,
              isExpandedCopy: component.isExpandedCopy
            });
          });
        }
      });
      
      // For each component that appears in multiple panels, draw connector lines
      componentPanelGroups.forEach((instances, componentId) => {
        if (instances.length > 1) {
          // Sort instances by panel x position
          instances.sort((a, b) => a.panel.x - b.panel.x);
          
          // Draw lines between consecutive instances
          for (let i = 0; i < instances.length - 1; i++) {
            const startInstance = instances[i];
            const endInstance = instances[i + 1];

            // Try to use DOM positions if available
            const startPositionKey = `${startInstance.component.id}-${startInstance.panel.id}`;
            const endPositionKey = `${endInstance.component.id}-${endInstance.panel.id}`;

            const startPosition = componentPositions.get(startPositionKey);
            const endPosition = componentPositions.get(endPositionKey);

            let startBounds = startPosition;
            let endBounds = endPosition;

            // Fallback to calculation if DOM positions not yet available
            if (!startBounds || !endBounds) {
              startBounds = startBounds || getFallbackComponentBounds(startInstance.panel, startInstance.component);
              endBounds = endBounds || getFallbackComponentBounds(endInstance.panel, endInstance.component);
            }

            const startY = startBounds.centerY;
            const endY = endBounds.centerY;
            const rawCategory = startInstance.component.iconCategory || startInstance.component.featureCategory || endInstance.component.iconCategory || endInstance.component.featureCategory;
            const expansionColor = getIconCategoryColor(rawCategory);
            
            // Attach expansion lines to the actual feature row bounds, not the panel edge.
            const startX = startBounds.rightX;
            const endX = endBounds.leftX;
            const midX = (startX + endX) / 2;
            
            // Draw stepped line connection (like main step connections)
            // 1. Horizontal line from component to midpoint
            connections.push({
              key: `expansion-line-${componentId}-${i}-h1`,
              x1: startX,
              y1: startY,
              x2: midX,
              y2: startY,
              isExpansionLine: true,
              strokeColor: expansionColor
            });
            
            // 2. Vertical line connecting the two heights
            connections.push({
              key: `expansion-line-${componentId}-${i}-v`,
              x1: midX,
              y1: startY,
              x2: midX,
              y2: endY,
              isExpansionLine: true,
              strokeColor: expansionColor
            });
            
            // 3. Horizontal line from midpoint to next component
            connections.push({
              key: `expansion-line-${componentId}-${i}-h2`,
              x1: midX,
              y1: endY,
              x2: endX,
              y2: endY,
              isExpansionLine: true,
              strokeColor: expansionColor
            });
          }
        }
      });
    }
    
    return connections;
  }, [flowItems, showExpansionLines, getSharedMainConnectorY, componentPositions]);

  const { dots: gridDots, width: safeGridWidth, height: safeGridHeight, minX, minY } = useMemo(
    () => generateGridDots(),
    [generateGridDots]
  );

  // Update expansion line positions from DOM after render
  useEffect(() => {
    if (!showExpansionLines) return;

    const getOffsetWithinAncestor = (element, ancestor) => {
      let top = 0;
      let left = 0;
      let current = element;

      while (current && current !== ancestor) {
        top += current.offsetTop;
        left += current.offsetLeft;
        current = current.offsetParent;
      }

      if (current !== ancestor) {
        return null;
      }

      return { top, left };
    };

    // Wait for next frame to ensure DOM is updated
    const frameId = requestAnimationFrame(() => {
      const panels = flowItems.filter(item => item.type === "panel");
      let updated = false;
      
      panels.forEach((panel) => {
        const panelEl = document.querySelector(`[data-panel-id="${panel.id}"]`);
        if (panelEl && panel.components) {
          panel.components.forEach((component) => {
            // Query component by its unique ID instead of index
            const componentEl = panelEl.querySelector(`[data-component-id="${component.id}"]`);
            if (componentEl) {
              const flowContainer = document.querySelector('.flow-container');
              if (flowContainer) {
                const rawOffset = getOffsetWithinAncestor(componentEl, flowContainer);
                if (!rawOffset) {
                  return;
                }

                const centerY = minY + rawOffset.top + (componentEl.offsetHeight / 2);
                const leftX = minX + rawOffset.left;
                const rightX = leftX + componentEl.offsetWidth;
                
                // Store position using component ID + panel ID as key
                const positionKey = `${component.id}-${panel.id}`;
                const existingPosition = componentPositions.get(positionKey);
                const hasChanged = !existingPosition ||
                  Math.abs(existingPosition.centerY - centerY) > 0.5 ||
                  Math.abs(existingPosition.leftX - leftX) > 0.5 ||
                  Math.abs(existingPosition.rightX - rightX) > 0.5;
                
                if (hasChanged) {
                  componentPositions.set(positionKey, { centerY, leftX, rightX });
                  updated = true;
                }
              }
            }
          });
        }
      });
      
      // Trigger re-render if positions were updated
      if (updated) {
        setDomPositionsReady(prev => !prev);
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [flowItems, showExpansionLines, componentPositions, minX, minY]);

  const connections = useMemo(
    () => {
      const rawConnections = generateConnections();
      // Offset all connection coordinates by minX/minY so they start from 0
      return rawConnections.map(conn => ({
        ...conn,
        x1: conn.x1 !== undefined && !isNaN(minX) ? conn.x1 - minX : conn.x1,
        y1: conn.y1 !== undefined && !isNaN(minY) ? conn.y1 - minY : conn.y1,
        x2: conn.x2 !== undefined && !isNaN(minX) ? conn.x2 - minX : conn.x2,
        y2: conn.y2 !== undefined && !isNaN(minY) ? conn.y2 - minY : conn.y2,
        x: conn.x !== undefined && !isNaN(minX) ? conn.x - minX : conn.x,
        y: conn.y !== undefined && !isNaN(minY) ? conn.y - minY : conn.y,
      }));
    },
    [generateConnections, minX, minY, domPositionsReady]
  );

  return {
    gridDots,
    safeGridWidth,
    safeGridHeight,
    minX,
    minY,
    connections,
    GRID_SIZE,
    CONTAINER_WIDTH,
    CONTAINER_HEIGHT
  };
};
